import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, ComponentRef, computed, effect, inject, input, OnDestroy, output, Signal, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { ResumeItemComponent } from '@webapp/core/ui/components/resume-item/resume-item.component';
import { UserResumeComponent } from '@webapp/core/ui/components/user-resume/user-resume.component';
import { ReferralCodeBannerComponent } from '@webapp/core/ui/components/referral-code-banner/referral-code-banner.component';
import { IUser } from '@webapp/users/interfaces/users.interface';
import { IResumeItem } from '@webapp/quoter/interfaces/quoter.interface';
import { CURRENCIES, ICoverage } from '@webapp/payments';
import { RiskRegistry, RiskUiSlot } from '@webapp/risk-core';
import { formatCurrency } from '@webapp/core';
import { CurrencyFormatPipe } from '@webapp/core/pipes/currencyFormat';

interface IInsurableData {
  [key: string]: any;
}

interface IRiskData {
  id?: string;
  name?: string;
  tag?: string;
}

interface IQuoteData {
  id?: string;
  internalNumber: number;
  quoteId?: string;
}

@Component({
  selector: 'app-flow-resume-step',
  imports: [
    ResumeItemComponent,
    UserResumeComponent,
    ReferralCodeBannerComponent,
    CurrencyFormatPipe
  ],
  providers: [DatePipe],
  templateUrl: './flow-resume-step.component.html',
  styleUrl: './flow-resume-step.component.scss',
})
export class FlowResumeStepComponent implements AfterViewInit, OnDestroy {

  private readonly riskRegistry = inject(RiskRegistry);
  // Bumped on every mount so a slow slot chunk cannot attach after a newer mount or destroy.
  private mountGeneration = 0;

  @ViewChild('detailContainer', { read: ViewContainerRef })
  private detailContainer?: ViewContainerRef;

  private dynamicRef: ComponentRef<unknown> | null = null;
  private clauseRemovedSub: Subscription | null = null;

  // Input properties for all data needed in the resume
  user = input<IUser | null>(null);
  insurableData = input<IInsurableData | null>(null);
  selectedCoverage = input<ICoverage | null>(null);
  additionalCoverage = input<ICoverage | null>(null);
  riskType = input<IRiskData | null>(null);
  quoteInfo = input<IQuoteData | null>(null);
  currency = input<string>('ARS');

  readonly detailComponentType = input<string | null>(null);
  readonly detailData = input<unknown>(null);
  readonly removable = input<boolean>(true);

  constructor() {
    effect(() => {
      // Read to re-run when the page flags a detail (re-mount on risk change).
      this.detailComponentType();
      this.mountDetail(this.detailData());
    });
  }

  ngAfterViewInit(): void {
    this.mountDetail(this.detailData());
  }

  ngOnDestroy(): void {
    this.mountGeneration++;
    this.clauseRemovedSub?.unsubscribe();
    this.clauseRemovedSub = null;
    this.dynamicRef?.destroy();
    this.dynamicRef = null;
  }

  /** Mounts the risk's `insurable-detail` slot; a risk without one shows no detail. */
  private mountDetail(data: unknown): void {
    const generation = ++this.mountGeneration;
    this.clauseRemovedSub?.unsubscribe();
    this.clauseRemovedSub = null;
    if (this.dynamicRef) {
      this.dynamicRef.destroy();
      this.dynamicRef = null;
    }
    const slot = this.riskRegistry.slot('web', this.riskType()?.tag ?? '', 'insurable-detail');
    if (slot && this.detailContainer) {
      void this.mountSlotDetail(slot, this.detailContainer, data, generation);
    }
  }

  private async mountSlotDetail(slot: RiskUiSlot, container: ViewContainerRef, data: unknown, generation: number): Promise<void> {
    try {
      const type = (await slot.load()) as Type<unknown>;
      if (generation !== this.mountGeneration) return;
      this.bindDetail(container.createComponent(type), data);
    } catch (error) {
      console.error('Error loading insurable-detail slot:', error);
    }
  }

  private bindDetail(ref: ComponentRef<unknown>, data: unknown): void {
    this.dynamicRef = ref;
    ref.setInput('data', data);
    ref.setInput('quoteId', this.quoteInfo()?.id ?? null);
    ref.setInput('removable', this.removable());
    const inst = ref.instance as { clauseRemoved?: { subscribe: (fn: (id: string) => void) => Subscription } };
    if (inst.clauseRemoved?.subscribe) {
      this.clauseRemovedSub = inst.clauseRemoved.subscribe((clauseId: string) => {
        this.clauseRemoved.emit(clauseId);
      });
    }
  }

  // ── Referral code ──────────────────────────────────────────────────────────

  readonly showReferralCode: Signal<boolean> = computed(() => {
    const tag = this.riskType()?.tag;
    if (!tag) return false;
    return this.riskRegistry.find(tag)?.capabilities.vehicle ?? false;
  });

  referralCodeChanged = output<string | null>();
  referralCodeValidating = output<boolean>();
  readonly clauseRemoved = output<string>();

  // ── End referral code ──────────────────────────────────────────────────────

  /**
   * Computed property that builds insurable data items using the risk plugin
   */
  protected readonly insurableItems: Signal<IResumeItem[]> = computed(() => {
    const insurable = this.insurableData();
    const risk = this.riskType();

    if (!insurable || !risk?.tag) {
      return [];
    }

    try {
      return this.riskRegistry.resolve(risk.tag).quoter.buildResumeItems(insurable);
    } catch (error) {
      console.warn('Error building resume items with the risk plugin:', error);
      return this.buildFallbackItems(insurable);
    }
  });



  /**
   * Computed property for coverage total amount.
   * Prefers USD if present in prices, falls back to ARS.
   */
  protected readonly coverageTotal: Signal<string> = computed(() => {
    const prices = this.selectedCoverage()?.prices;
    if (!prices?.length) return '';

    const price = prices.find(p => p.currency === CURRENCIES.USD)
      ?? prices.find(p => p.currency === CURRENCIES.ARS)
      ?? prices[0];
    const value = price.subscription || price.premium;
    if (!value) return '';
    return formatCurrency(value, price.currency ?? CURRENCIES.ARS);
  });

  /**
   * Computed property for additional coverage total amount.
   * Prefers USD if present in prices, falls back to ARS.
   */
  protected readonly additionalCoverageTotal: Signal<string> = computed(() => {
    const prices = this.additionalCoverage()?.prices;
    if (!prices?.length) return '';

    const price = prices.find(p => p.currency === CURRENCIES.USD)
      ?? prices.find(p => p.currency === CURRENCIES.ARS)
      ?? prices[0];
    const value = price.subscription || price.premium;
    if (!value) return '';
    return formatCurrency(value, price.currency ?? CURRENCIES.ARS);
  });

  /**
   * Computed property for total amount including additional coverage
   */
  protected readonly grandTotal: Signal<string> = computed(() => {
    const coverage = this.selectedCoverage();
    const additionalCoverage = this.additionalCoverage();
    const currency = this.currency();

    const mainPremium = coverage?.prices?.[0]?.premium || 0;
    const additionalPremium = additionalCoverage?.prices?.[0]?.premium || 0;

    const total = mainPremium + additionalPremium;

    if (total <= 0) {
      return '';
    }

    return formatCurrency(total, currency);
  });

  /**
   * Fallback method for building resume items when strategy fails
   */
  private buildFallbackItems(insurable: IInsurableData): IResumeItem[] {
    const items: IResumeItem[] = [];

    const commonFields = [
      { key: 'activity', title: 'Actividad' },
      { key: 'brand', title: 'Marca' },
      { key: 'model', title: 'Modelo' },
      { key: 'year', title: 'Año' },
      { key: 'plate', title: 'Patente' },
      { key: 'goodsType', title: 'Tipo de mercadería' },
      { key: 'origin', title: 'Origen' },
      { key: 'destination', title: 'Destino' }
    ];

    commonFields.forEach(field => {
      if (insurable[field.key]) {
        items.push({
          title: field.title,
          text: insurable[field.key].toString()
        });
      }
    });

    this.addSumInsuredItem(items, insurable);

    return items;
  }

  /**
   * Helper to add sum insured item if available
   */
  private addSumInsuredItem(items: IResumeItem[], insurable: IInsurableData): void {
    if (insurable['sumInsured']) {
      const formattedAmount = formatCurrency(insurable['sumInsured'], this.currency());
      if (formattedAmount) {
        items.push({ title: 'Suma asegurada', text: formattedAmount });
      }
    }
  }


}
